import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionStatusApi, loginApi } from '../http/http';
import classes from './SubscriptionPendingPage.module.css';

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 24;

const readFlow = () => {
    try {
        const flow = JSON.parse(sessionStorage.getItem('subscriptionFlow') || 'null');
        return flow?.msisdn && flow?.packType && flow?.trxId && flow?.startedAt ? flow : null;
    } catch {
        return null;
    }
};

const SubscriptionPendingPage = () => {
    const navigate = useNavigate();
    const [flow] = useState(readFlow);
    const [status, setStatus] = useState(flow ? 'PENDING' : 'FAILED');
    const [message, setMessage] = useState(flow ? 'Waiting for subscription confirmation...' : 'Your subscription session is unavailable.');
    const [error, setError] = useState(flow ? '' : 'Please start a new subscription.');
    const pollCount = useRef(0);

    useEffect(() => {
        if (!flow) {
            navigate('/subscribe', { replace: true });
            return undefined;
        }

        let cancelled = false;
        let timer;

        const checkStatus = async () => {
            try {
                const response = await getSubscriptionStatusApi(flow.msisdn, flow.packType);
                const data = response.data?.data || {};
                const currentState = String(data.state || data.status || '').toUpperCase();

                if (cancelled) return;

                setStatus(currentState || 'PENDING');

                if (currentState === 'ACTIVE' || data.isActive === true) {
                    const loginResponse = await loginApi(flow.msisdn);
                    const loginData = loginResponse.data?.data || {};
                    localStorage.setItem('token', loginData.token);
                    localStorage.setItem('userData', JSON.stringify(loginData.user || { msisdn: flow.msisdn }));
                    sessionStorage.removeItem('subscriptionFlow');
                    navigate('/', { replace: true });
                    return;
                }

                if (['FAILED', 'FAILURE', 'REJECTED', 'ERROR', 'INACTIVE'].includes(currentState)) {
                    setMessage('VDL could not confirm this subscription.');
                    setError(data.failureReason || data.reason || 'Please retry the subscription.');
                    return;
                }

                setMessage('Waiting for VDL confirmation...');
                pollCount.current += 1;
                if (pollCount.current >= MAX_POLLS) {
                    setStatus('TIMEOUT');
                    setMessage('Subscription confirmation timed out.');
                    setError('Please retry to start a new subscription attempt.');
                    return;
                }

                timer = window.setTimeout(checkStatus, POLL_INTERVAL_MS);
            } catch (requestError) {
                if (cancelled) return;
                setError(requestError.response?.data?.message || 'Unable to fetch the VDL status right now.');
                pollCount.current += 1;
                if (pollCount.current < MAX_POLLS) {
                    timer = window.setTimeout(checkStatus, POLL_INTERVAL_MS);
                }
            }
        };

        checkStatus();

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [flow, navigate]);

    const retry = () => {
        pollCount.current = 0;
        setError('');
        sessionStorage.removeItem('subscriptionFlow');
        navigate('/subscribe', { replace: true });
    };

    return (
        <main className={classes.page_wrapper}>
            <section className={classes.card} aria-live="polite">
                <div className={classes.logo_area}>
                    <div className={classes.logo_icon_wrap}>
                        <img src="/assets/images/logo.png" alt="GamePlex logo" className={classes.logo_icon} />
                    </div>
                    <p className={classes.logo_text}>Game<span>Plex</span></p>
                </div>

                <div className={classes.spinner} aria-label="Loading" />

                <div className={classes.content_area}>
                    <p className={classes.status_label}>{status}</p>
                    <h1 className={classes.title}>Confirming your subscription</h1>
                    <p className={classes.subtitle}>{message}</p>
                    <p className={classes.detail}>Please keep this page open while we receive the VDL callback.</p>
                </div>

                {error && (
                    <div className={classes.error_area}>
                        <p>{error}</p>
                        <button type="button" className={classes.cta_btn} onClick={retry}>Check Again</button>
                    </div>
                )}
            </section>
        </main>
    );
};

export default SubscriptionPendingPage;
